import { Plus } from "lucide-react";
import React from "react";

import { cn } from "@repo/design-system/utils";

type AddOfferButtonSize = "sm" | "md";

export type AddOfferButtonProps = {
  /** `sm` for the dense desktop comparison rows, `md` for the mobile rows. */
  size?: AddOfferButtonSize;
  disabled?: boolean;
  /** Accessible label - the button is icon-only. */
  label?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
};

// Sizes are ~27% under the round Tailwind steps (28/32/36px) they were tuned
// at, so the button stays compact inside the dense comparison rows. The whole
// row is clickable, so the button itself does not have to carry the tap target.
// Radius and glyph scale with the box so the proportions stay put.
const sizeClasses: Record<AddOfferButtonSize, string> = {
  sm: "h-[20.5px] w-[20.5px] rounded-[0.36rem] xl:h-[23.5px] xl:w-[23.5px] xl:rounded-[0.45rem]",
  md: "h-[26.4px] w-[26.4px] rounded-[0.58rem]"
};

const iconClasses: Record<AddOfferButtonSize, string> = {
  sm: "h-[12.6px] w-[12.6px] xl:h-[14.4px] xl:w-[14.4px]",
  md: "h-[16.2px] w-[16.2px]"
};

/**
 * The "add this supplier's offer" affordance used across the supplier price
 * comparison box. Icon-only by design (the rows are dense), so it leans on
 * size, colour and motion to read as the primary action of each row.
 */
const AddOfferButton = React.memo(
  ({
    size = "sm",
    disabled = false,
    label = "Add to cart",
    onClick,
    className
  }: AddOfferButtonProps) => {
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          "relative inline-flex flex-shrink-0 items-center justify-center",
          "transition-all duration-200 ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2",
          sizeClasses[size],
          disabled
            ? "cursor-not-allowed bg-gray-200 text-gray-400 shadow-none"
            : [
                "cursor-pointer text-white",
                "bg-gradient-to-b from-purple-500 to-purple-700",
                "shadow-[0_1px_2px_rgba(88,28,135,0.25),0_4px_10px_-2px_rgba(147,51,234,0.45)]",
                // Inner top highlight - gives the flat gradient a soft, raised edge.
                "before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit]",
                "before:bg-gradient-to-b before:from-white/25 before:to-transparent",
                // Row hover lifts the button too, so the whole line reads as one target.
                "group-hover:from-purple-400 group-hover:to-purple-600",
                "group-hover:shadow-[0_2px_4px_rgba(88,28,135,0.28),0_8px_18px_-4px_rgba(147,51,234,0.6)]",
                "hover:-translate-y-px hover:from-purple-400 hover:to-purple-600",
                "hover:shadow-[0_2px_4px_rgba(88,28,135,0.28),0_10px_20px_-4px_rgba(147,51,234,0.65)]",
                "active:translate-y-0 active:scale-95",
                "active:shadow-[0_1px_2px_rgba(88,28,135,0.35)]"
              ],
          className
        )}
      >
        <Plus
          className={cn("relative", iconClasses[size])}
          strokeWidth={2.75}
          aria-hidden="true"
        />
      </button>
    );
  }
);
AddOfferButton.displayName = "AddOfferButton";

export { AddOfferButton };
